/**
 * Values a later email (or the lender's own draft) states for this deal that
 * are held off it for a person to decide (rule R5): `pending_revisions` on the
 * deal read (`GET /loans/{id}`).
 *
 * The deal keeps its current value — blank included — until someone applies
 * the revision in the record's Email Thread panel. A screen that shows the
 * deal's value must still say one is waiting: The Marlowe's lender-draft floor,
 * fees and covenants read "—" and "No covenants stored" with no sign of them,
 * and the parallel run's gap log recorded them as misses.
 *
 * Presentation only. Nothing here applies a revision or counts one as data.
 */
import type { RawDealFile } from '@/services/dealFile'

export type HeldRevision = {
  /** The field the revision names, and the catalogue slot it would fill
   *  (`loan_field` is `custom_fields.<name>` or a native column). */
  fields: string[]
  incoming: unknown
  ingestionId: string | null
  /** The lender's own draft terms, not a newer statement of the deal. */
  lenderDraft: boolean
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** The deal's held revisions, whichever casing the payload arrived in. */
export function heldRevisions(raw: RawDealFile | undefined | null): HeldRevision[] {
  const rows = (raw?.pending_revisions ?? raw?.pendingRevisions) as unknown
  if (!Array.isArray(rows)) return []
  return rows.flatMap((row): HeldRevision[] => {
    if (!row || typeof row !== 'object') return []
    const r = row as Record<string, unknown>
    const slot = str(r.loan_field) ?? str(r.loanField)
    const fields = [str(r.field), slot ? slot.replace(/^custom_fields\./, '') : null]
      .filter((name): name is string => Boolean(name))
    if (fields.length === 0) return []
    const incoming = r.incoming
    if (incoming === null || incoming === undefined || incoming === '') return []
    return [{
      fields,
      incoming,
      ingestionId: str(r.ingestion_id) ?? str(r.ingestionId),
      lenderDraft: r.lender_draft === true || r.lenderDraft === true,
    }]
  })
}

/** The newest held revision for any of `names` (the list is oldest email first). */
export function heldFor(held: HeldRevision[], names: readonly string[]): HeldRevision | undefined {
  for (let i = held.length - 1; i >= 0; i -= 1) {
    if (held[i].fields.some((name) => names.includes(name))) return held[i]
  }
  return undefined
}

/** Whose value it is and where it waits: "lender draft (INQ-…), awaiting review". */
export function heldBasis(entry: HeldRevision): string {
  const who = entry.lenderDraft ? 'lender draft' : 'later email'
  return `${who}${entry.ingestionId ? ` (${entry.ingestionId})` : ''}, awaiting review`
}

/** The muted line a held value reads as beside the deal's own. */
export function heldLine(entry: HeldRevision, shown: string): string {
  return `Held: ${shown} — ${heldBasis(entry)}`
}
